// Карточка задачи (решение владельца 2026-09-08): открывается кликом по № задачи
// и по ячейке «по связи» в «Модели ГПР». Вкладки:
//   «Задача» — правки расчёта: длительность, сдвиг начала, ручные даты, объём,
//              приоритет, бригада, подрядчик, исключение из модели;
//   «Расчёт сроков» (бывшие «Предшественники», 2026-09-15) — слева как получилось
//              начало, справа окончание (GET /tasks/<tid>/explain, грузится только
//              на этой вкладке); ниже связи из справочника (можно отключить на этой
//              задаче) и ручные связи с конкретными задачами, там же они добавляются;
//   «Работа» — та же карточка работы, что открывается по названию работы;
//   «Схема» — чертёж с захватками и закрашенным (только у задач, к которым применена
//              схема, #109): смотреть рядом с задачей, править — на экране «Схемы».
// Показывается двумя способами (просьба владельца 2026-09-14): как окно поверх
// таблицы (как было) и как нижняя панель под таблицей — проп `vnizu`. Во втором
// случае таблица остаётся на виду: щёлкнул строку — панель переключилась на неё.
// Переключатель «Детали внизу» — в шапке «Модели»; выключил его — снова окно.
import { useCallback, useEffect, useState } from 'react';
import { call, p, type Data } from './api';
import { confirmBox } from './dialog';
import { PoleSpiska } from './Podskazki';
import { WorkCard } from './WorkCard';
import { ShemaZadachi } from './screens/Shemy';
import { FloorCard } from './FloorCard';
import { fmt, parseDate } from './util';

type Tasks = Data<'/api/projects/{pid}/tasks', 'get'>;
type Row = Tasks['rows'][number];
type Floor = Data<'/api/projects/{pid}/structure', 'get'>[string][number];
type LinksGet = Data<'/api/tasks/{tid}/links', 'get'>;
type FaktZapis = Data<'/api/projects/{pid}/fakt/log', 'get'>[number];
type Explain = Data<'/api/tasks/{tid}/explain', 'get'>;
type Punkt = Explain['kandidaty'][number];
type Vkladka = 'zadacha' | 'svyazi' | 'rabota' | 'summa' | 'etazh' | 'fakt' | 'shema';

/** список пунктов разбора «Расчёт сроков»: ✓ — действует, ✕ — не подействовало */
function Punkty({ zag, spisok, off = false }: { zag: string; spisok: Punkt[]; off?: boolean }) {
  if (!spisok.length) return null;
  return (
    <div className="tc-pk">
      <div className="tc-pk-z">{zag}</div>
      {spisok.map((x, i) => (
        <div key={i} className={'tc-pk-r' + (x.deystvuet ? ' on' : '') + (off ? ' off' : '')}>
          <span className="tc-pk-m">{off ? '✕' : x.deystvuet ? '✓' : '·'}</span>
          <span className="tc-pk-n">{x.nazvanie}</span>
          <span className="tc-pk-v">
            {(x.znachenie ?? '') + (x.data ? (x.znachenie ? ' · ' : '') + fmt(x.data) : '')}</span>
          <span className="tc-pk-p">{x.poyasnenie ?? ''}</span>
        </div>
      ))}
    </div>
  );
}

export function TaskCard({ pid, row, korpusa, works, struct, podr, can, vkladka = 'zadacha',
  ruchnaya = false, vnizu = false, onClose, onChanged, onFail }: {
  pid: number; row: Row; korpusa: string[]; works: string[]; struct: Record<string, Floor[]>;
  podr: string[]; can: boolean; vkladka?: Vkladka;
  /** задача заведена руками (у работы раскатка «вручную» / контур «Ручной») —
   *  только такую можно удалить поштучно (2026-09-10) */
  ruchnaya?: boolean;
  /** нижняя панель под таблицей вместо окна поверх неё (2026-09-14) */
  vnizu?: boolean;
  onClose: () => void; onChanged: (msg?: string) => void; onFail: (m: string) => void;
}) {
  const [tab, setTab] = useState<Vkladka>(vkladka);
  const [d, setD] = useState<LinksGet | null>(null);
  const [t, setT] = useState<Row>(row);
  // история отметок факта по задаче (#9, 2026-09-12): кто, в какой роли и когда
  // поставил или снял статус и даты — чтобы было видно, чья дата стоит
  const [istoriya, setIstoriya] = useState<FaktZapis[]>([]);
  const [err, setErr] = useState('');
  // разбор «Расчёт сроков» (2026-09-15): пересчитывает проект в памяти (в СБ3 ~2–3 с),
  // поэтому грузится только на своей вкладке; ex_tick — перечитать после правки связей
  const [ex, setEx] = useState<Explain | null>(null);
  const [exGruzim, setExGruzim] = useState(false);
  const [exTick, setExTick] = useState(0);
  useEffect(() => {
    if (tab !== 'svyazi') return;
    let zhivo = true;
    setExGruzim(true);
    void (async () => {
      const r = await call<Explain>('get', p('/api/tasks/{tid}/explain', { tid: row.id }));
      if (!zhivo) return;
      setExGruzim(false);
      if (r.ok) setEx(r.data); else setErr(r.error.message);
    })();
    return () => { zhivo = false; };
  }, [tab, row.id, exTick]);
  const [f, setF] = useState({ korpus: row.korpus || korpusa[0] || '', rabota: '',
    etazh: row.etazh ? String(row.etazh) : '', tip: 'ОН', zapas: '', soft: false });

  const reload = useCallback(async () => {
    const [l, tk, fl] = await Promise.all([
      call<LinksGet>('get', p('/api/tasks/{tid}/links', { tid: row.id })),
      call<Tasks>('get', p('/api/projects/{pid}/tasks', { pid })
        + '?korpus=' + encodeURIComponent(row.korpus || '') + '&order=name&limit=20000'),
      call<FaktZapis[]>('get', p('/api/projects/{pid}/fakt/log', { pid }) + '?task=' + row.id),
    ]);
    if (l.ok) setD(l.data);
    if (fl.ok) setIstoriya(fl.data);
    if (tk.ok) {
      const svezh = tk.data.rows.find(x => x.id === row.id);
      if (svezh) setT(svezh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);
  useEffect(() => { void reload(); }, [reload]);

  /** удалить задачу целиком (только ручную, 2026-09-10): раскатанная вернётся
   *  при следующем событии раскатки, для неё есть «Исключить из модели» */
  async function udalit() {
    const da = await confirmBox('Удалить задачу № ' + (t.nomer ?? '') + '?', {
      podpis: t.rabota + ' · ' + (t.korpus || '') + (t.etazh ? ' · эт. ' + t.etazh : '')
        + '\n\nВместе с ней удалятся её ручные связи, отметки факта и базовые даты.'
        + ' Это необратимо; отмене (стрелкой «назад») удаление не подлежит.',
      ok: 'Удалить', opasno: true,
    });
    if (!da) return;
    const r = await call('delete', p('/api/tasks/{tid}', { tid: row.id }));
    if (!r.ok) { setErr(r.error.message); onFail(r.error.message); return; }
    onChanged('Задача удалена. Нажмите «Актуализировать», чтобы обновить даты.');
    onClose();
  }

  const dd = (x?: string | null) => (x ? fmt(x) : '—');

  /** правка поля задачи; даты принимаем как ДД.ММ.ГГГГ */
  async function patch(pole: string, znach: string | number | boolean | null, data = true) {
    let v: string | number | boolean | null = znach;
    if (data && typeof znach === 'string' && znach.trim() !== '') {
      const iso = parseDate(znach);
      if (iso === undefined) { setErr('Дата не разобрана: нужно ДД.ММ.ГГГГ'); return; }
      v = iso;
    }
    if (v === '') v = null;
    const r = await call<{ rev: number }>('patch', p('/api/tasks/{tid}', { tid: t.id }),
      { rev: t.rev, [pole]: v });
    if (!r.ok) { setErr(r.error.message); return; }
    setErr('');
    await reload();
    setExTick(x => x + 1);
    onChanged('Задача изменена. Нажмите «Актуализировать», чтобы обновить даты.');
  }

  async function toggleOtkl(pred: string, otkl: boolean) {
    const r = await call('post', p('/api/tasks/{tid}/links/otkl', { tid: t.id }), { pred, otkl });
    if (!r.ok) { onFail(r.error.message); return; }
    await reload();
    setExTick(x => x + 1);
    onChanged(otkl ? 'Связь отключена на этой задаче.' : 'Связь снова действует.');
  }
  async function delRuch(id: number) {
    const r = await call('delete', p('/api/task-links/{lid}', { lid: id }));
    if (!r.ok) { onFail(r.error.message); return; }
    await reload();
    setExTick(x => x + 1);
    onChanged('Ручной предшественник убран.');
  }
  async function addRuch() {
    if (!f.rabota.trim() || !f.korpus) { setErr('Укажите работу и корпус предшественника'); return; }
    const r = await call('post', p('/api/tasks/{tid}/links', { tid: t.id }), {
      korpus: f.korpus, rabota: f.rabota.trim(), etazh: f.etazh || null,
      tip: f.tip, zapas: f.zapas.trim() || null, soft: f.soft });
    if (!r.ok) { setErr(r.error.message); return; }
    setErr('');
    setF(s => ({ ...s, rabota: '', zapas: '' }));
    await reload();
    setExTick(x => x + 1);
    onChanged('Ручной предшественник назначен. Нажмите «Актуализировать».');
  }

  const pole = (label: string, uzel: JSX.Element, hint?: string) => (
    <div className="wc-pole" key={label} title={hint}>
      <span className="wc-lab">{label}</span>
      {uzel}
    </div>
  );
  const vvod = (znach: string | number | null | undefined, na: string, data = false, hint?: string) => (
    <input defaultValue={znach ?? ''} disabled={!can} placeholder={data ? 'ДД.ММ.ГГГГ' : ''} title={hint}
      onBlur={e => { const v = e.target.value.trim(); if (v !== String(znach ?? '')) void patch(na, v, data); }} />
  );

  return (
    <Obolochka vnizu={vnizu} onClose={onClose}>
        {/* одна строка вместо трёх (владелец 2026-09-15): слева вкладки, справа —
            заголовок и крестик. Подзаголовок со сроками убран: те же сроки стоят
            в полях вкладки «Задача» и в строке таблицы, откуда карточку открыли */}
        <div className="wc-tabs wc-shapka">
          {/* «Схема» (#109, 2026-09-19) показывается только у задач с захватками:
              у остальных вкладка была бы всегда пустой */}
          {(([['zadacha', 'Задача'], ['rabota', 'Работа'], ['summa', 'Суммарная работа'], ['etazh', 'Этаж'],
             ['svyazi', 'Расчёт сроков'], ['fakt', 'Факт']] as [Vkladka, string][])
            .concat(row.shema ? [['shema', 'Схема']] : []))
            .map(([k, nz]) => (
              <button key={k} className={'sec' + (tab === k ? ' on' : '')}
                onClick={() => setTab(k)}>{nz}</button>
            ))}
          <div className="wc-name wc-name-ug"
            title={(t.korpus || '') + (t.etazh ? ' · этаж ' + t.etazh : ' · корпус целиком')
              + ' · расчёт ' + dd(t.nachalo) + ' – ' + dd(t.okonchanie)
              + ' · длительность ' + (t.dlit ?? '—') + ' дн'
              + (t.iskl ? ' · исключена из модели' : '')}>
            {'№ ' + (t.nomer ?? '—') + ' · ' + (t.rabota || '')}
          </div>
          <a className="wc-x" title="Закрыть (Esc)" onClick={onClose}>✕</a>
        </div>
        <div className="wc-body">
          {err && <div className="err">{err}</div>}

          {tab === 'zadacha' && (
            <>
              <div className="wc-polya">
                {pole('Длительность, дн', vvod(t.dlit_r, 'dlit_ruchnaya', false),
                  'Ручная длительность именно этой задачи; пусто — из справочника работы'
                  + (t.dlit_avto != null ? ' (' + t.dlit_avto + ' дн)' : ''))}
                {pole('Сдвиг начала, дн', vvod(t.zapas, 'zapas', false),
                  'Запас: на сколько дней задача начинается позже, чем позволяет расчёт')}
                {pole('Начало', vvod(fmt(t.nach_vr), 'nachalo_vruchnuyu', true),
                  'Ручное начало: фиксирует задачу на эту дату')}
                {pole('Окончание', vvod(fmt(t.okonch_vr), 'okonchanie_vruchnuyu', true),
                  'Ручное окончание: фиксирует задачу')}
                {pole('Объём', vvod(t.obyom, 'obyom', false),
                  'Длительность = объём ÷ (выработка × численность) из карточки работы')}
                {pole('Приоритет', vvod(t.prior, 'prioritet_vruchnuyu', false),
                  'Место в очереди бригады: меньше — раньше')}
                {pole('Бригада', vvod(t.brigada, 'nomer_brigady', false),
                  'Номер бригады: разные бригады идут параллельно')}
                {pole('Подрядчик', (
                  <select value={t.podryadchik ?? ''} disabled={!can}
                    onChange={e => void patch('podryadchik', e.target.value || null, false)}>
                    <option value="">— не назначен —</option>
                    {podr.map(x => <option key={x}>{x}</option>)}
                  </select>))}
                {pole('Исключить из модели', (
                  <label className="wc-tk" title={t.iskl_etazh
                    ? 'Этаж этой задачи исключён из расчёта целиком — вернуть можно только этаж, в его окне на «Этажах»'
                    : 'Задача не получает дат и не занимает бригаду'}>
                    <input type="checkbox" checked={!!t.iskl} disabled={!can || !!t.iskl_etazh}
                      onChange={e => void patch('isklyuchena', e.target.checked, false)} />
                    {t.iskl_etazh ? 'да — этаж вне расчёта' : 'да'}
                  </label>))}
              </div>
              <div className="pw-muted" style={{ marginTop: 10 }}>
                Ручные значения фиксируют строку: расчёт их не перебивает. После правок —
                «Актуализировать».
              </div>
              {can && (
                <div className="bar actbar" style={{ marginTop: 10 }}>
                  {ruchnaya ? (
                    <button className="dlg-opasno" onClick={() => void udalit()}>Удалить задачу</button>
                  ) : (
                    <span className="pw-muted">
                      Задачу создала раскатка работы, поштучно она не удаляется: уберите корпус
                      из карточки работы или поставьте галочку «Исключить из модели» выше.
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {tab === 'svyazi' && (
            <>
              {/* «Расчёт сроков» (владелец 2026-09-15): левая половина — как получилось
                  начало, правая — окончание; все влияющие поля и решения модели */}
              <div className="tc-raschet">
                <section className="tc-rz">
                  <div className="tc-rz-h">Начало <b>{dd(ex?.nachalo ?? t.nachalo)}</b>
                    <span className="pw-muted">{ex?.nachalo_itog ?? ''}</span></div>
                  {exGruzim && !ex && <div className="pw-muted">Считаю расчёт проекта…</div>}
                  {ex && <>
                    <Punkty zag="Кандидаты — начало = самый поздний" spisok={ex.kandidaty} />
                    <Punkty zag="Не подействовало" spisok={ex.ne_deystvuyut} off />
                    <Punkty zag="Решения модели" spisok={ex.resheniya} />
                  </>}
                </section>
                <section className="tc-rz">
                  <div className="tc-rz-h">Окончание <b>{dd(ex?.okonchanie ?? t.okonchanie)}</b>
                    <span className="pw-muted">{ex?.okonchanie_itog ?? ''}</span></div>
                  {ex && <>
                    <Punkty zag="Длительность — действует отмеченная" spisok={ex.dlitelnost} />
                    <Punkty zag="Факт" spisok={ex.fakt} />
                  </>}
                </section>
              </div>
              <div className="wc-podzag">Предшественники задачи</div>
              {/* таблица предшественников по образцу MS Project (владелец 2026-09-15):
                  кто предшественник, какая связь, какие у него сроки. Первый блок —
                  связи справочника (их можно отключить на этой задаче галочкой),
                  второй — ручные связи именно этой задачи */}
              <table className="tc-preds">
                <thead>
                  <tr>
                    <th className="tc-p-d" title="Связь действует на этой задаче">Действ.</th>
                    <th>Предшественник</th>
                    <th className="tc-p-m">Корпус, этаж</th>
                    <th className="tc-p-m">Тип связи</th>
                    <th className="tc-p-n">Задержка</th>
                    <th className="tc-p-dt">Начало</th>
                    <th className="tc-p-dt">Окончание</th>
                    <th className="tc-p-x"></th>
                  </tr>
                </thead>
                <tbody>
                  {!d?.shtatnye.length && !d?.ruchnye.length && (
                    <tr><td colSpan={8} className="muted">Предшественников нет.</td></tr>
                  )}
                  {(d?.shtatnye || []).map((s, i) => (
                    <tr key={'s' + i} className={s.otkl ? 'tc-off' : undefined}>
                      <td className="tc-p-d">
                        <input type="checkbox" checked={!s.otkl} disabled={!can}
                          title="снять галочку — на этой задаче связь не действует"
                          onChange={e => void toggleOtkl(s.pred, !e.target.checked)} />
                      </td>
                      <td title={s.pred}>{s.pred}</td>
                      <td className="tc-p-m">
                        {s.pred_task
                          ? (s.pred_task.korpus || '') + (s.pred_task.etazh
                              ? ' · эт. ' + s.pred_task.etazh : ' · корпус целиком')
                          : <span className="pw-muted">задачи нет</span>}
                      </td>
                      <td className="tc-p-m" title={s.tip === 'НН' ? 'начало → начало' : 'окончание → начало'}>
                        {s.tip === 'НН' ? 'нач. → нач.' : 'оконч. → нач.'}
                        {s.soft ? ', мягкая' : ''}
                        {s.oper ? ', опережение +' + s.oper + ' эт.' : ''}
                      </td>
                      <td className="tc-p-n">{s.zapas ? s.zapas + ' дн' : ''}</td>
                      <td className="tc-p-dt">{s.pred_task ? dd(s.pred_task.nachalo) : '—'}</td>
                      <td className="tc-p-dt">{s.pred_task ? dd(s.pred_task.okonchanie) : '—'}</td>
                      <td className="tc-p-x"><span className="pw-muted" title="связь из справочника работ">спр.</span></td>
                    </tr>
                  ))}
                  {(d?.ruchnye || []).map(m => (
                    <tr key={'r' + m.id}>
                      <td className="tc-p-d"><input type="checkbox" checked readOnly disabled
                        title="ручная связь действует всегда; убрать её — крестиком справа" /></td>
                      <td title={m.rabota}>{m.nomer != null ? '№' + m.nomer + ' · ' : ''}{m.rabota}</td>
                      <td className="tc-p-m" style={m.iskl ? { color: 'var(--plot-danger)' } : undefined}>
                        {m.korpus + (m.etazh ? ' · эт. ' + m.etazh : ' · корпус целиком')}
                        {m.iskl ? ' · исключена!' : ''}
                      </td>
                      <td className="tc-p-m">
                        {m.tip === 'НН' ? 'нач. → нач.' : 'оконч. → нач.'}{m.soft ? ', мягкая' : ''}
                      </td>
                      <td className="tc-p-n">{m.zapas ? m.zapas + ' дн' : ''}</td>
                      <td className="tc-p-dt">{dd(m.nachalo)}</td>
                      <td className="tc-p-dt">{dd(m.okonchanie)}</td>
                      <td className="tc-p-x">
                        {can && <a title="убрать ручного предшественника" className="tc-x"
                          onClick={() => void delRuch(m.id)}>×</a>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {can && (
                <>
                  <div className="wc-podzag">Назначить ручного предшественника</div>
                  <div className="bar" style={{ margin: '2px 0 8px' }}>
                    <select value={f.korpus} title="Корпус предшественника"
                      onChange={e => setF(s => ({ ...s, korpus: e.target.value, etazh: '' }))}>
                      <option value=""></option>{korpusa.map(k => <option key={k} value={k}>{k}</option>)}</select>
                    <PoleSpiska value={f.rabota} opts={works} placeholder="работа" style={{ width: 190 }}
                      onChange={v => setF(s => ({ ...s, rabota: v }))} onEnter={() => void addRuch()} />
                    <select value={f.etazh} title="Этаж предшественника"
                      onChange={e => setF(s => ({ ...s, etazh: e.target.value }))}>
                      <option value="">— корпус целиком —</option>
                      {(struct[f.korpus] || []).map(fl => <option key={fl.id} value={String(fl.etazh)}>{fl.etazh}</option>)}</select>
                    <select value={f.tip} title="Тип связи"
                      onChange={e => setF(s => ({ ...s, tip: e.target.value }))}>
                      <option value="ОН">оконч. → нач.</option><option value="НН">нач. → нач.</option></select>
                    <input placeholder="задержка, дн" style={{ width: 110 }} value={f.zapas}
                      onChange={e => setF(s => ({ ...s, zapas: e.target.value }))} />
                    <label className="pw-muted" title="можно идти вперёд, если предшественник отстаёт">
                      <input type="checkbox" style={{ width: 'auto' }} checked={f.soft}
                        onChange={e => setF(s => ({ ...s, soft: e.target.checked }))} /> мягкая</label>
                    <button onClick={() => void addRuch()}>Назначить</button>
                  </div>
                  <div className="pw-muted">Ручная связь ведёт на конкретную задачу — можно на другой
                    этаж или корпус. После правок — «Актуализировать».</div>
                </>
              )}
            </>
          )}

          {/* «Факт» (владелец 2026-09-15): всё, что известно о факте задачи —
              статус и даты, процент, подрядчик, комментарий бригады, откуда
              взялся статус и полный журнал отметок с автором и ролью (#9).
              Правится факт в «Шахматке факта» и кликом по ячейкам «Факт» в
              модели, здесь — только показ, чтобы карточка ничего не перетирала */}
          {tab === 'shema' && <ShemaZadachi tid={t.id} />}

          {tab === 'fakt' && (
            <>
              <div className="wc-polya">
                {pole('Статус', <b>{t.sv || (t.status ? t.status + ' (из слепка СБ3)' : '—')}</b>)}
                {pole('Начало по факту', <b>{dd(t.vn)}</b>)}
                {pole('Окончание по факту', <b>{dd(t.vo)}</b>)}
                {pole('Готовность', <b>{t.proc != null ? t.proc + ' %' : '—'}</b>)}
                {pole('Подрядчик', <b>{t.podryadchik || '—'}</b>)}
                {pole('План', <span>{dd(t.nachalo)} – {dd(t.okonchanie)}</span>,
                  'Расчётные сроки задачи — с чем сравнивать факт')}
              </div>
              {t.kf && <div className="wc-podzag">Комментарий: {t.kf}</div>}
              <div className="wc-istoriya">
                <b>Кто и когда отмечал</b>
                {!istoriya.length && (
                  <div className="pw-muted">Отметок факта по задаче не было{t.status && !t.sv
                    ? '; статус «' + t.status + '» пришёл из слепка СБ3. «Очистить факт» снимет его — задача посчитается заново'
                    : ''}.</div>
                )}
                {istoriya.map((z, i) => (
                  <div key={i} className="wc-ist-str">
                    <span className="pw-muted">{z.vremya}</span>
                    {' '}<b>{z.kto}</b>{z.rol ? <span className="pw-muted"> · {z.rol}</span> : null}
                    {': '}{z.status || 'факт снят'}
                    {z.nachalo ? ', начало ' + fmt(z.nachalo) : ''}
                    {z.okonchanie ? ', окончание ' + fmt(z.okonchanie) : ''}
                    {z.procent != null ? ', ' + z.procent + ' %' : ''}
                    {z.kommentarij ? ' — ' + z.kommentarij : ''}
                  </div>
                ))}
              </div>
              <div className="pw-muted" style={{ marginTop: 8 }}>
                Отмечают факт в «Шахматке факта» и кликом по ячейкам группы «Факт» в модели.
              </div>
            </>
          )}

          {tab === 'rabota' && t.rabota && (
            <WorkCard pid={pid} workName={t.rabota} can={can} vstroennaya tolkoRabota={vnizu}
              onClose={onClose} onChanged={m => onChanged(m)} />
          )}

          {/* «Суммарная работа» (владелец 2026-09-15): та же вкладка карточки работы —
              по корпусам задачи, сроки и количество бригад */}
          {tab === 'summa' && t.rabota && (
            <WorkCard pid={pid} workName={t.rabota} can={can} vstroennaya odnaVkladka="summa"
              onClose={onClose} onChanged={m => onChanged(m)} />
          )}

          {/* вкладка «Этаж» (владелец 2026-09-15): та же карточка этажа, что по клику на номер */}
          {tab === 'etazh' && (t.korpus && t.etazh != null && t.etazh !== ''
            ? <FloorCard key={t.korpus + '¶' + t.etazh} pid={pid} korpus={t.korpus} etazh={String(t.etazh)}
                can={can} vstroennaya onClose={onClose} onChanged={m => onChanged(m)} />
            : <div className="muted">У этой задачи нет этажа — она ведётся на корпус целиком
                {t.korpus ? ' (' + t.korpus + ')' : ''}.</div>)}
        </div>
    </Obolochka>
  );
}

/** Где живёт карточка: окном поверх таблицы (как было) или нижней панелью
 *  под ней (2026-09-14). Панель рисует сам экран «Модели» — здесь только
 *  содержимое, без затемнения и без ловушки Esc: её ставит панель. */
function Obolochka({ vnizu, onClose, children }:
  { vnizu: boolean; onClose: () => void; children: React.ReactNode }) {
  if (vnizu) return <div className="tc-vnizu">{children}</div>;
  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dlg wc" role="dialog" aria-modal="true"
        onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>
        {children}
      </div>
    </div>
  );
}
